import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Pencil, Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { API_URL, errMsg, fmtH, godzinyApi } from './shared';
import { ConflictWarning } from './PlacePicker';
import { CorrectDialog, RejectDialog } from './ReviewDialogs';
import { ManualEntryDialog } from './ManualEntryDialog';
import { MissingHoursBox } from './MissingHoursBox';

const EntryRow = ({ e, canManage, checked, onCheck, onApprove, onCorrect, onReject }) => (
  <div className="py-3 border-b border-[var(--crm-border)]/50 space-y-2" data-testid={`pending-entry-${e.id}`}>
    <div className="flex flex-wrap items-center gap-3">
      {canManage && <Checkbox checked={checked} disabled={e.needs_assignment} onCheckedChange={onCheck} data-testid={`pending-check-${e.id}`} />}
      <span className="font-mono text-sm text-[var(--crm-text-primary)]">{e.start}–{e.end}</span>
      <span className="text-xs text-[var(--crm-text-muted)]">przerwa {e.break_min || 0} min</span>
      <span className="font-semibold text-[#FF6B35]">{fmtH(e.hours)}</span>
      <span className={`text-sm ${e.needs_assignment ? 'text-amber-600' : 'text-[var(--crm-text-secondary)]'}`}>
        {e.place}{e.unlisted_address ? `, ${e.unlisted_address}` : ''}
      </span>
      {e.note && <span className="text-xs italic text-[var(--crm-text-muted)]">„{e.note}”</span>}
      {canManage && (
        <div className="ml-auto flex gap-1">
          <Button size="sm" onClick={onApprove} disabled={e.needs_assignment} className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`pending-approve-${e.id}`} title={e.needs_assignment ? 'Najpierw przypisz zlecenie lub kategorię' : ''}>
            <Check className="w-4 h-4 mr-1" />Zatwierdź
          </Button>
          <Button size="sm" variant="outline" onClick={onCorrect} data-testid={`pending-correct-${e.id}`}><Pencil className="w-4 h-4 mr-1" />Popraw i zatwierdź</Button>
          <Button size="sm" variant="outline" onClick={onReject} className="text-red-500" data-testid={`pending-reject-${e.id}`}><X className="w-4 h-4 mr-1" />Odrzuć</Button>
        </div>
      )}
    </div>
    <ConflictWarning conflicts={e.conflicts} testid={`pending-conflict-${e.id}`} />
  </div>
);

export const PendingTab = ({ canManage, onCountChange }) => {
  const [groups, setGroups] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [sel, setSel] = useState({});
  const [dialog, setDialog] = useState(null);
  const load = useCallback(() => {
    godzinyApi.pending().then(setGroups).catch(() => toast.error('Błąd pobierania wpisów'));
    onCountChange?.();
    window.dispatchEvent(new Event('godziny:pending-changed'));
  }, [onCountChange]);
  useEffect(() => { load(); godzinyApi.workers().then(setWorkers).catch(() => {}); }, [load]);

  const approve = async (id) => {
    try { await axios.post(`${API_URL}/godziny/entries/${id}/approve`, {}); toast.success('Zatwierdzono'); load(); }
    catch (e) { toast.error(errMsg(e, 'Błąd')); }
  };
  const ids = Object.keys(sel).filter((k) => sel[k]);
  const bulk = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/godziny/entries/bulk-approve`, { ids });
      toast.success(`Zatwierdzono ${data.approved} z ${ids.length}`);
      setSel({});
      load();
    } catch (e) { toast.error(errMsg(e, 'Błąd zatwierdzania')); }
  };

  return (
    <div className="space-y-4" data-testid="pending-tab">
      <MissingHoursBox canManage={canManage} refreshKey={groups} onAdd={(w, date) => setDialog({ type: 'manual', workerId: w.id, date })} />
      {canManage && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setDialog({ type: 'manual' })} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white" data-testid="manual-entry-btn">
            <Plus className="w-4 h-4 mr-1" />Dodaj wpis ręcznie
          </Button>
          <Button variant="outline" onClick={bulk} disabled={!ids.length} data-testid="bulk-approve-btn">
            <Check className="w-4 h-4 mr-1" />Zatwierdź zaznaczone ({ids.length})
          </Button>
        </div>
      )}
      {groups.length === 0 && <p className="text-[var(--crm-text-muted)]" data-testid="pending-empty">Brak wpisów do akceptacji.</p>}
      {groups.map((g) => (
        <Card key={`${g.worker_id}-${g.date}`} className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4" data-testid={`pending-group-${g.worker_id}-${g.date}`}>
          <p className="font-semibold text-[var(--crm-text-primary)]">{g.worker_name} · {g.date}
            <span className="ml-2 text-sm text-[var(--crm-text-secondary)]">{fmtH(g.entries.reduce((s, e) => s + e.hours, 0))}</span></p>
          {g.entries.map((e) => (
            <EntryRow key={e.id} e={e} canManage={canManage} checked={!!sel[e.id]} onCheck={(v) => setSel({ ...sel, [e.id]: v })}
              onApprove={() => approve(e.id)} onCorrect={() => setDialog({ type: 'correct', entry: e })}
              onReject={() => setDialog({ type: 'reject', entry: e })} />
          ))}
        </Card>
      ))}
      {dialog?.type === 'manual' && <ManualEntryDialog workers={workers} initialWorkerId={dialog.workerId} initialDate={dialog.date} onClose={() => setDialog(null)} onSaved={load} />}
      {dialog?.type === 'correct' && <CorrectDialog entry={dialog.entry} onClose={() => setDialog(null)} onDone={load} />}
      {dialog?.type === 'reject' && <RejectDialog entry={dialog.entry} onClose={() => setDialog(null)} onDone={load} />}
    </div>
  );
};
